import { useState } from "react";
import { Calculator, TrendingDown, TrendingUp, AlertTriangle, Info } from "lucide-react";
import { Slider } from "@/components/ui/slider";

export const BounceRateCalculator = () => {
  const [listSize, setListSize] = useState(10000);
  const [listAge, setListAge] = useState(12); // months
  const [verificationRate, setVerificationRate] = useState(0); // % verified
  const [engagementRate, setEngagementRate] = useState(20); // % active in last 90 days
  const [sourceQuality, setSourceQuality] = useState(70); // quality score 0-100

  // Calculate estimated bounce rate
  const calculateBounceRate = () => {
    // Base decay rate: ~2.5% of emails go bad per month
    const decayRate = 0.025;
    const ageDecay = Math.min(listAge * decayRate, 0.5); // Max 50% from age

    // Verification reduces bounces significantly
    const verificationReduction = verificationRate * 0.008; // Up to 80% reduction

    // Engagement indicates list health
    const engagementFactor = (100 - engagementRate) * 0.003; // Low engagement = higher bounces

    // Source quality impact
    const sourceImpact = (100 - sourceQuality) * 0.002;

    // Calculate total
    let bounceRate = 2 + (ageDecay * 100) + engagementFactor + sourceImpact - (verificationReduction * 100);
    bounceRate = Math.max(0.5, Math.min(bounceRate, 40)); // Clamp between 0.5% and 40%

    return bounceRate;
  };

  const estimatedBounceRate = calculateBounceRate();
  const estimatedBounces = Math.round((estimatedBounceRate / 100) * listSize);

  const getHealthStatus = (rate: number) => {
    if (rate <= 2) return { label: "Excellent", color: "text-success", bg: "bg-success/10" };
    if (rate <= 5) return { label: "Good", color: "text-success", bg: "bg-success/10" };
    if (rate <= 10) return { label: "Moderate", color: "text-warning", bg: "bg-warning/10" };
    return { label: "Poor", color: "text-destructive", bg: "bg-destructive/10" };
  };

  const health = getHealthStatus(estimatedBounceRate);

  const recommendations = [];
  if (verificationRate < 50) {
    recommendations.push("Verify your email list with an email verification service to reduce bounces by up to 80%");
  }
  if (listAge > 6 && engagementRate < 30) {
    recommendations.push("Run a re-engagement campaign to identify inactive subscribers");
  }
  if (engagementRate < 20) {
    recommendations.push("Remove subscribers who haven't engaged in 12+ months");
  }
  if (sourceQuality < 60) {
    recommendations.push("Review your list acquisition sources and remove low-quality leads");
  }
  if (estimatedBounceRate > 5) {
    recommendations.push("Consider warming up your sending gradually to protect sender reputation");
  }

  return (
    <div className="section-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Calculator className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h3 className="font-display text-lg font-semibold text-foreground">
            Bounce Rate Calculator
          </h3>
          <p className="text-sm text-muted-foreground">
            Estimate deliverability based on list hygiene practices
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input Sliders */}
        <div className="space-y-6">
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-foreground">List Size</label>
              <span className="text-sm text-primary font-medium">{listSize.toLocaleString()}</span>
            </div>
            <Slider
              value={[listSize]}
              onValueChange={([v]) => setListSize(v)}
              min={100}
              max={100000}
              step={100}
            />
            <p className="text-xs text-muted-foreground mt-1">Total email addresses in your list</p>
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-foreground">List Age</label>
              <span className="text-sm text-primary font-medium">{listAge} months</span>
            </div>
            <Slider
              value={[listAge]}
              onValueChange={([v]) => setListAge(v)}
              min={1}
              max={36}
              step={1}
            />
            <p className="text-xs text-muted-foreground mt-1">How long since emails were collected</p>
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Verification Rate</label>
              <span className="text-sm text-primary font-medium">{verificationRate}%</span>
            </div>
            <Slider
              value={[verificationRate]}
              onValueChange={([v]) => setVerificationRate(v)}
              min={0}
              max={100}
              step={5}
            />
            <p className="text-xs text-muted-foreground mt-1">Percentage verified with email validation service</p>
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Engagement Rate (90 days)</label>
              <span className="text-sm text-primary font-medium">{engagementRate}%</span>
            </div>
            <Slider
              value={[engagementRate]}
              onValueChange={([v]) => setEngagementRate(v)}
              min={0}
              max={100}
              step={5}
            />
            <p className="text-xs text-muted-foreground mt-1">% who opened/clicked in last 90 days</p>
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <label className="text-sm font-medium text-foreground">Source Quality</label>
              <span className="text-sm text-primary font-medium">{sourceQuality}/100</span>
            </div>
            <Slider
              value={[sourceQuality]}
              onValueChange={([v]) => setSourceQuality(v)}
              min={0}
              max={100}
              step={5}
            />
            <p className="text-xs text-muted-foreground mt-1">Quality of lead sources (double opt-in = 100, purchased = 20)</p>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {/* Main Result */}
          <div className={`rounded-xl border p-5 ${health.bg} border-current/20`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground">Estimated Bounce Rate</p>
                <p className={`text-4xl font-bold ${health.color}`}>
                  {estimatedBounceRate.toFixed(1)}%
                </p>
              </div>
              <div className="text-right">
                <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${health.bg} ${health.color}`}>
                  {estimatedBounceRate > 5 ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                  {health.label}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-current/10">
              <div>
                <p className="text-xs text-muted-foreground">Est. Bounces</p>
                <p className="text-lg font-semibold text-foreground">{estimatedBounces.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Deliverable</p>
                <p className="text-lg font-semibold text-foreground">{(listSize - estimatedBounces).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Benchmarks */}
          <div className="bg-muted/50 rounded-xl p-4">
            <h4 className="text-sm font-medium text-foreground mb-3">Industry Benchmarks</h4>
            <div className="space-y-2">
              {[
                { label: "Excellent", range: "< 2%", color: "bg-success" },
                { label: "Acceptable", range: "2-5%", color: "bg-success/60" },
                { label: "Needs Work", range: "5-10%", color: "bg-warning" },
                { label: "Critical", range: "> 10%", color: "bg-destructive" },
              ].map((bench) => (
                <div key={bench.label} className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${bench.color}`} />
                  <span className="text-xs text-foreground flex-1">{bench.label}</span>
                  <span className="text-xs text-muted-foreground">{bench.range}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                <Info className="w-4 h-4 text-primary" />
                Recommendations
              </h4>
              <ul className="space-y-1.5">
                {recommendations.map((rec, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
